/**
 * The permission catalogue and role matrix.
 *
 * This file is the single source of truth for authorization. The API guard
 * reads it, the web app's `<PermissionGate>` reads it, and the database seed
 * writes it. There is deliberately no second copy anywhere — a permission that
 * exists in the UI but not the guard is a security hole, and the only reliable
 * way to prevent that is to make them the same object.
 *
 * Key format: `module:resource:action`.
 */
export const PERMISSIONS = {
    // ── NOC & dashboard ──────────────────────────────────────────────────────
    NOC_DASHBOARD_READ: 'noc:dashboard:read',
    NOC_TOPOLOGY_READ: 'noc:topology:read',
    NOC_EVENT_READ: 'noc:event:read',
    // ── Monitoring ───────────────────────────────────────────────────────────
    DEVICE_READ: 'monitoring:device:read',
    DEVICE_WRITE: 'monitoring:device:write',
    DEVICE_DELETE: 'monitoring:device:delete',
    DEVICE_POLL: 'monitoring:device:poll',
    DEVICE_CREDENTIAL_WRITE: 'monitoring:credential:write',
    METRIC_READ: 'monitoring:metric:read',
    ALARM_READ: 'monitoring:alarm:read',
    ALARM_ACK: 'monitoring:alarm:ack',
    ALARM_CLEAR: 'monitoring:alarm:clear',
    ALARM_DEF_READ: 'monitoring:alarm-def:read',
    ALARM_DEF_WRITE: 'monitoring:alarm-def:write',
    MAINTENANCE_READ: 'monitoring:maintenance:read',
    MAINTENANCE_WRITE: 'monitoring:maintenance:write',
    DISCOVERY_RUN: 'monitoring:discovery:run',
    // ── Internet & backbone ──────────────────────────────────────────────────
    INTERNET_READ: 'internet:status:read',
    INTERNET_WRITE: 'internet:upstream:write',
    // ── GIS, fiber, plant ────────────────────────────────────────────────────
    GIS_READ: 'gis:feature:read',
    GIS_WRITE: 'gis:feature:write',
    // Sites are the inventory every other module hangs off: devices, network
    // elements, alarm filters, maintenance targets and row-level scoping all key
    // on a site id. Read is therefore granted as widely as DEVICE_READ; write is
    // an administrative act, kept beside the other infrastructure writes.
    SITE_READ: 'gis:site:read',
    SITE_WRITE: 'gis:site:write',
    FIBER_READ: 'gis:fiber:read',
    FIBER_WRITE: 'gis:fiber:write',
    SPLICE_READ: 'gis:splice:read',
    SPLICE_WRITE: 'gis:splice:write',
    POLE_READ: 'gis:pole:read',
    POLE_WRITE: 'gis:pole:write',
    PHOTO_UPLOAD: 'gis:photo:write',
    // ── Subscribers (read-only mirror of UISP) ───────────────────────────────
    SUBSCRIBER_READ: 'subscribers:subscriber:read',
    /** Contact details and address. Separated so a Viewer can see counts but not people. */
    SUBSCRIBER_PII_READ: 'subscribers:pii:read',
    // ── Incidents ────────────────────────────────────────────────────────────
    INCIDENT_READ: 'incidents:incident:read',
    INCIDENT_CREATE: 'incidents:incident:create',
    INCIDENT_UPDATE: 'incidents:incident:update',
    INCIDENT_ASSIGN: 'incidents:incident:assign',
    INCIDENT_ESCALATE: 'incidents:incident:escalate',
    INCIDENT_RESOLVE: 'incidents:incident:resolve',
    INCIDENT_CLOSE: 'incidents:incident:close',
    INCIDENT_MERGE: 'incidents:incident:merge',
    INCIDENT_REVIEW_WRITE: 'incidents:review:write',
    // ── Field operations ─────────────────────────────────────────────────────
    JOB_READ: 'field:job:read',
    /** Read every job regardless of assignment. Without it, row scope applies. */
    JOB_READ_ALL: 'field:job:read-all',
    JOB_CREATE: 'field:job:create',
    JOB_UPDATE: 'field:job:update',
    JOB_ASSIGN: 'field:job:assign',
    JOB_COMPLETE: 'field:job:complete',
    // ── Assets ───────────────────────────────────────────────────────────────
    ASSET_READ: 'assets:asset:read',
    ASSET_WRITE: 'assets:asset:write',
    ASSET_DELETE: 'assets:asset:delete',
    // ── Capacity, analytics, reports ─────────────────────────────────────────
    CAPACITY_READ: 'capacity:forecast:read',
    ANALYTICS_READ: 'analytics:dashboard:read',
    REPORT_READ: 'reports:report:read',
    REPORT_RUN: 'reports:report:run',
    /** Distinct from run: exports leave the system and are audited with row counts. */
    REPORT_EXPORT: 'reports:report:export',
    REPORT_DEFINE: 'reports:definition:write',
    // ── Automation ───────────────────────────────────────────────────────────
    AUTOMATION_READ: 'automation:rule:read',
    AUTOMATION_WRITE: 'automation:rule:write',
    AUTOMATION_RUN: 'automation:rule:run',
    /** Gate on any mutating action against the network. */
    AUTOMATION_APPROVE: 'automation:run:approve',
    // ── AI operations ────────────────────────────────────────────────────────
    AI_USE: 'ai:assistant:use',
    AI_CONFIGURE: 'ai:config:write',
    // ── Administration ───────────────────────────────────────────────────────
    USER_READ: 'admin:user:read',
    USER_WRITE: 'admin:user:write',
    ROLE_WRITE: 'admin:role:write',
    AUDIT_READ: 'admin:audit:read',
    SETTING_READ: 'admin:setting:read',
    SETTING_WRITE: 'admin:setting:write',
    NOTIFICATION_READ: 'admin:notification:read',
    NOTIFICATION_WRITE: 'admin:notification:write',
    INTEGRATION_READ: 'admin:integration:read',
    INTEGRATION_RUN: 'admin:integration:run',
    INTEGRATION_WRITE: 'admin:integration:write',
    SYSTEM_HEALTH_READ: 'admin:system:read',
};
export const ALL_PERMISSIONS = Object.values(PERMISSIONS);
export const ROLES = {
    SUPER_ADMIN: 'SUPER_ADMIN',
    NETWORK_ADMIN: 'NETWORK_ADMIN',
    NOC_ENGINEER: 'NOC_ENGINEER',
    FIELD_TECH: 'FIELD_TECH',
    MANAGER: 'MANAGER',
    VIEWER: 'VIEWER',
};
const P = PERMISSIONS;
/** Read-only permissions shared by every role above Viewer. */
const BASE_READ = [
    P.NOC_DASHBOARD_READ,
    P.NOC_TOPOLOGY_READ,
    P.NOC_EVENT_READ,
    P.DEVICE_READ,
    P.METRIC_READ,
    P.ALARM_READ,
    P.INCIDENT_READ,
    P.GIS_READ,
    P.SITE_READ,
    P.INTERNET_READ,
];
/**
 * Role → permissions.
 *
 * Deliberate omissions worth naming, because they are the ones people ask
 * about:
 *
 * · NETWORK_ADMIN has no user administration. Separating "can reconfigure the
 *   network" from "can grant themselves more access" is the point of having
 *   two admin roles at all.
 * · NOC_ENGINEER cannot write device configuration or credentials. They run
 *   the incident lifecycle; they do not reconfigure routers mid-shift.
 * · MANAGER has no operational writes at all — read plus export.
 * · VIEWER has no subscriber PII and no export. A dashboard on a wall does not
 *   need customer names on it.
 */
export const ROLE_PERMISSIONS = {
    [ROLES.SUPER_ADMIN]: ALL_PERMISSIONS,
    [ROLES.NETWORK_ADMIN]: [
        ...BASE_READ,
        P.DEVICE_WRITE,
        P.DEVICE_DELETE,
        P.DEVICE_POLL,
        P.DEVICE_CREDENTIAL_WRITE,
        P.ALARM_ACK,
        P.ALARM_CLEAR,
        P.ALARM_DEF_READ,
        P.ALARM_DEF_WRITE,
        P.MAINTENANCE_READ,
        P.MAINTENANCE_WRITE,
        P.DISCOVERY_RUN,
        P.INTERNET_WRITE,
        P.GIS_WRITE,
        P.SITE_WRITE,
        P.FIBER_READ,
        P.FIBER_WRITE,
        P.SPLICE_READ,
        P.SPLICE_WRITE,
        P.POLE_READ,
        P.POLE_WRITE,
        P.PHOTO_UPLOAD,
        P.SUBSCRIBER_READ,
        P.SUBSCRIBER_PII_READ,
        P.INCIDENT_CREATE,
        P.INCIDENT_UPDATE,
        P.INCIDENT_ASSIGN,
        P.INCIDENT_ESCALATE,
        P.INCIDENT_RESOLVE,
        P.INCIDENT_CLOSE,
        P.INCIDENT_MERGE,
        P.INCIDENT_REVIEW_WRITE,
        P.JOB_READ,
        P.JOB_READ_ALL,
        P.JOB_CREATE,
        P.JOB_UPDATE,
        P.JOB_ASSIGN,
        P.ASSET_READ,
        P.ASSET_WRITE,
        P.CAPACITY_READ,
        P.ANALYTICS_READ,
        P.REPORT_READ,
        P.REPORT_RUN,
        P.REPORT_EXPORT,
        P.AUTOMATION_READ,
        P.AUTOMATION_WRITE,
        P.AUTOMATION_RUN,
        P.AUTOMATION_APPROVE,
        P.AI_USE,
        P.NOTIFICATION_READ,
        P.NOTIFICATION_WRITE,
        P.INTEGRATION_READ,
        P.INTEGRATION_RUN,
        P.SYSTEM_HEALTH_READ,
        P.AUDIT_READ,
    ],
    [ROLES.NOC_ENGINEER]: [
        ...BASE_READ,
        P.DEVICE_POLL,
        P.ALARM_ACK,
        P.ALARM_CLEAR,
        P.ALARM_DEF_READ,
        P.MAINTENANCE_READ,
        P.MAINTENANCE_WRITE,
        P.FIBER_READ,
        P.SPLICE_READ,
        P.POLE_READ,
        P.SUBSCRIBER_READ,
        P.SUBSCRIBER_PII_READ,
        P.INCIDENT_CREATE,
        P.INCIDENT_UPDATE,
        P.INCIDENT_ASSIGN,
        P.INCIDENT_ESCALATE,
        P.INCIDENT_RESOLVE,
        P.INCIDENT_CLOSE,
        P.INCIDENT_MERGE,
        P.INCIDENT_REVIEW_WRITE,
        P.JOB_READ,
        P.JOB_READ_ALL,
        P.JOB_CREATE,
        P.JOB_ASSIGN,
        P.ASSET_READ,
        P.CAPACITY_READ,
        P.ANALYTICS_READ,
        P.REPORT_READ,
        P.REPORT_RUN,
        P.AUTOMATION_READ,
        P.AUTOMATION_RUN,
        P.AI_USE,
        P.NOTIFICATION_READ,
        P.INTEGRATION_READ,
        P.SYSTEM_HEALTH_READ,
    ],
    // Row-scoped: JOB_READ without JOB_READ_ALL means own jobs only.
    [ROLES.FIELD_TECH]: [
        P.NOC_DASHBOARD_READ,
        P.DEVICE_READ,
        P.ALARM_READ,
        P.INCIDENT_READ,
        P.GIS_READ,
        P.FIBER_READ,
        P.SPLICE_READ,
        P.SPLICE_WRITE,
        P.POLE_READ,
        P.POLE_WRITE,
        P.PHOTO_UPLOAD,
        P.SUBSCRIBER_READ,
        P.SUBSCRIBER_PII_READ,
        P.JOB_READ,
        P.JOB_UPDATE,
        P.JOB_COMPLETE,
        P.ASSET_READ,
    ],
    [ROLES.MANAGER]: [
        ...BASE_READ,
        P.FIBER_READ,
        P.SPLICE_READ,
        P.POLE_READ,
        P.SUBSCRIBER_READ,
        P.SUBSCRIBER_PII_READ,
        P.JOB_READ,
        P.JOB_READ_ALL,
        P.ASSET_READ,
        P.CAPACITY_READ,
        P.ANALYTICS_READ,
        P.REPORT_READ,
        P.REPORT_RUN,
        P.REPORT_EXPORT,
        P.REPORT_DEFINE,
        P.AUDIT_READ,
        P.AI_USE,
        P.MAINTENANCE_READ,
        P.AUTOMATION_READ,
        P.SYSTEM_HEALTH_READ,
    ],
    [ROLES.VIEWER]: [
        P.NOC_DASHBOARD_READ,
        P.NOC_EVENT_READ,
        P.DEVICE_READ,
        P.METRIC_READ,
        P.ALARM_READ,
        P.INCIDENT_READ,
        P.INTERNET_READ,
        P.GIS_READ,
        P.SITE_READ,
    ],
};
/** Roles for which MFA enrolment is mandatory. Mirrors MFA_REQUIRED_ROLES. */
/**
 * Roles for which two-factor authentication is mandatory.
 *
 * **Empty by default: MFA is opt-in.** Any user may enrol from
 * Profile → Two-factor authentication, and once they have, every sign-in
 * challenges them — enrolling is what turns it on, not a role.
 *
 * This is a deployment policy, so the real value comes from
 * `MFA_REQUIRED_ROLES` in the environment and this constant is only the
 * fallback. It previously listed SUPER_ADMIN and NETWORK_ADMIN *and*
 * disagreed with the env default, which meant `/auth/me` reported a mandate
 * the login path did not enforce — the web app then redirected to enrolment
 * on every navigation for accounts the API was happy to let through.
 *
 * To mandate it again, set `MFA_REQUIRED_ROLES=SUPER_ADMIN,NETWORK_ADMIN`.
 */
export const MFA_MANDATORY_ROLES = [];
export const ROLE_LABELS = {
    [ROLES.SUPER_ADMIN]: 'Super Administrator',
    [ROLES.NETWORK_ADMIN]: 'Network Administrator',
    [ROLES.NOC_ENGINEER]: 'NOC Engineer',
    [ROLES.FIELD_TECH]: 'Field Technician',
    [ROLES.MANAGER]: 'Manager',
    [ROLES.VIEWER]: 'Viewer',
};
/**
 * Effective permissions for a set of roles.
 *
 * Union, then deny-list. There is no per-user grant: permissions come from
 * roles only. Individual overrides are how a permission model becomes
 * unauditable within a year.
 */
export function resolvePermissions(roleKeys) {
    const result = new Set();
    for (const key of roleKeys) {
        const perms = ROLE_PERMISSIONS[key];
        if (perms)
            for (const p of perms)
                result.add(p);
    }
    return result;
}
/**
 * Explicit type guard rather than `instanceof Set` or `Array.isArray`.
 *
 * Neither narrows this union: `instanceof Set` cannot exclude `ReadonlySet`
 * from the negative branch, and `Array.isArray` cannot exclude a *readonly*
 * array (it guards `any[]`, which a readonly array is not). Testing for the
 * method is unambiguous and needs no casts at the call sites.
 */
function isPermissionSet(value) {
    return typeof value.has === 'function';
}
export function hasPermission(granted, required) {
    return isPermissionSet(granted) ? granted.has(required) : granted.includes(required);
}
export function hasAllPermissions(granted, required) {
    return required.every((r) => hasPermission(granted, r));
}
/** The module a permission belongs to, derived from its key. */
export function permissionModule(key) {
    return key.split(':')[0] ?? 'unknown';
}
//# sourceMappingURL=permissions.js.map